import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { DeepAnalysisResult, SiteAnalysisResult, NewBonusSection, LegacyBonusSection } from '@/types/analysisTypes';

function scoreColor(score: number): string {
  if (score >= 70) return '#2e7d32';
  if (score >= 40) return '#ff9900';
  return '#dc3545';
}

/**
 * Generate a PDF report from the deep analysis results
 */
export function generatePdfReport(result: DeepAnalysisResult, pageUrl: string): any {
  const categoryRows = Object.entries(result.category_scores).map(([key, value]) => {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const score = typeof value === 'number' ? value : value.score;
    const desc = typeof value === 'number' ? '' : (value.description || '');
    return [label, `${score}/20`, desc];
  });

  const fixesBody = result.actionable_fixes?.length
    ? result.actionable_fixes.map(f => [f.current_issue, f.suggested_fix, f.rationale])
    : null;

  const shareRows = Object.entries(result.shareability_scores).map(([key, value]) => [
    key.charAt(0).toUpperCase() + key.slice(1),
    `${typeof value === 'number' ? value : value.score}/100`,
    typeof value === 'number' ? 'N/A' : value.explanation,
    typeof value === 'number' ? 'N/A' : value.improvement,
  ]);

  const isNewFormat = (section: any): section is NewBonusSection =>
    section.expert_insight && 'title' in section.expert_insight;

  const bonusRows = buildBonusRows(result.bonus_section, isNewFormat(result.bonus_section));

  const docDefinition: any = {
    content: [
      { text: 'Content Analysis Report', style: 'title' },
      { text: `URL: ${pageUrl}`, style: 'meta' },
      { text: `Generated: ${new Date().toLocaleDateString()}`, style: 'meta', margin: [0, 0, 0, 10] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineColor: '#cccccc' }], margin: [0, 0, 0, 10] },
      {
        columns: [
          { text: 'Overall Score', style: 'heading' },
          { text: `${result.overall_score}/100`, style: 'bigScore', color: scoreColor(result.overall_score) },
        ],
        margin: [0, 0, 0, 10],
      },
      { text: 'Category Scores', style: 'heading' },
      {
        table: {
          headerRows: 1,
          widths: [100, 50, '*'],
          body: [
            [{ text: 'Category', style: 'tableHeader' }, { text: 'Score', style: 'tableHeader' }, { text: 'Description', style: 'tableHeader' }],
            ...categoryRows,
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 15],
      },
      { text: 'Recommended Improvements', style: 'heading' },
      fixesBody
        ? {
            table: {
              headerRows: 1,
              widths: ['*', '*', '*'],
              body: [
                [{ text: 'Current Issue', style: 'tableHeader' }, { text: 'Suggested Fix', style: 'tableHeader' }, { text: 'Rationale', style: 'tableHeader' }],
                ...fixesBody,
              ],
            },
            layout: 'lightHorizontalLines',
            margin: [0, 0, 0, 15],
          }
        : { text: 'No improvement suggestions available.', style: 'meta', margin: [0, 0, 0, 15] },
      { text: 'Audience Shareability Analysis', style: 'heading', pageBreak: 'before' },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', '*', '*'],
          body: [
            [{ text: 'Audience', style: 'tableHeader' }, { text: 'Score', style: 'tableHeader' }, { text: 'Explanation', style: 'tableHeader' }, { text: 'Improvement', style: 'tableHeader' }],
            ...shareRows,
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 15],
      },
      { text: 'Expert Creative Insights', style: 'heading' },
      {
        table: {
          headerRows: 1,
          widths: [80, 80, '*'],
          body: [
            [{ text: 'Insight Type', style: 'tableHeader' }, { text: isNewFormat(result.bonus_section) ? 'Title' : 'Question', style: 'tableHeader' }, { text: isNewFormat(result.bonus_section) ? 'Content' : 'Example/Answer', style: 'tableHeader' }],
            ...bonusRows,
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 15],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      text: `Generated by SEO Content Analyzer | Page ${currentPage} of ${pageCount}`,
      alignment: 'center',
      fontSize: 8,
      color: '#999999',
      margin: [0, 10],
    }),
    styles: {
      title: { fontSize: 20, bold: true, color: '#2c3e50', margin: [0, 0, 0, 5] },
      meta: { fontSize: 10, color: '#646464' },
      heading: { fontSize: 16, bold: true, color: '#2c3e50', margin: [0, 10, 0, 5] },
      bigScore: { fontSize: 28, bold: true, alignment: 'right' },
      tableHeader: { bold: true, fontSize: 10, color: '#ffffff', fillColor: '#2c3e50' },
    },
    defaultStyle: { fontSize: 10 },
  };

  return docDefinition;
}

function buildBonusRows(bonusSection: any, isNew: boolean): string[][] {
  const keys = ['expert_insight', 'unexpected_analogy', 'challenged_assumption', 'insider_tip'];
  return keys
    .filter(k => bonusSection[k])
    .map(key => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const value = bonusSection[key];
      if (isNew) {
        const title = value?.title || '';
        let content = '';
        if (key === 'expert_insight') content = `${value.intro || ''}\n\nKey takeaways: ${(value.key_takeaways || []).join(', ')}`;
        else if (key === 'unexpected_analogy') content = `${value.intro || ''}\n\nKey points: ${(value.key_points || []).join(', ')}`;
        else if (key === 'challenged_assumption') content = `${value.intro || ''}\n\nKey reasons: ${(value.key_reasons || []).join(', ')}`;
        else if (key === 'insider_tip') content = `${value.intro || ''}\n\nStep by step: ${(value.step_by_step || []).join(', ')}`;
        return [label, title, content];
      } else {
        if (value && typeof value === 'object' && typeof value !== 'string') {
          return [label, value.question || '', value.example || ''];
        }
        return [label, '', ''];
      }
    });
}

/**
 * Generate a PDF report for site analysis
 */
export function generateSitePdfReport(result: SiteAnalysisResult, siteName: string): any {
  const categoryRows = Object.entries(result.siteCategoryScores).map(([key, score]) => {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return [label, `${Math.round(score)}/100`];
  });

  const pagesRows = Object.entries(result.pageResults)
    .sort((a, b) => b[1].overall_score - a[1].overall_score)
    .map(([url, analysis]) => {
      const displayUrl = url.length > 60 ? url.substring(0, 57) + '...' : url;
      return [displayUrl, `${Math.round(analysis.overall_score)}/100`];
    });

  const docDefinition: any = {
    content: [
      { text: 'Site Analysis Report', style: 'title' },
      { text: `Site: ${siteName}`, style: 'meta' },
      { text: `Generated: ${new Date().toLocaleDateString()}`, style: 'meta', margin: [0, 0, 0, 10] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineColor: '#cccccc' }], margin: [0, 0, 0, 10] },
      {
        columns: [
          { text: 'Overall Site Score', style: 'heading' },
          { text: `${Math.round(result.siteOverallScore)}/100`, style: 'bigScore', color: scoreColor(result.siteOverallScore) },
        ],
        margin: [0, 0, 0, 10],
      },
      { text: 'Site Category Scores', style: 'heading' },
      {
        table: {
          headerRows: 1,
          widths: [250, 100],
          body: [
            [{ text: 'Category', style: 'tableHeader' }, { text: 'Score', style: 'tableHeader' }],
            ...categoryRows,
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 15],
      },
      { text: 'Analyzed Pages', style: 'heading' },
      {
        table: {
          headerRows: 1,
          widths: ['*', 80],
          body: [
            [{ text: 'Page URL', style: 'tableHeader' }, { text: 'Score', style: 'tableHeader' }],
            ...pagesRows,
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 15],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      text: `Generated by SEO Content Analyzer | Page ${currentPage} of ${pageCount}`,
      alignment: 'center',
      fontSize: 8,
      color: '#999999',
      margin: [0, 10],
    }),
    styles: {
      title: { fontSize: 20, bold: true, color: '#2c3e50', margin: [0, 0, 0, 5] },
      meta: { fontSize: 10, color: '#646464' },
      heading: { fontSize: 16, bold: true, color: '#2c3e50', margin: [0, 10, 0, 5] },
      bigScore: { fontSize: 28, bold: true, alignment: 'right' },
      tableHeader: { bold: true, fontSize: 10, color: '#ffffff', fillColor: '#2c3e50' },
    },
    defaultStyle: { fontSize: 10 },
  };

  return docDefinition;
}

/**
 * Export page recommendations to PDF
 */
export function exportPageRecommendationsToPDF(result: DeepAnalysisResult, pageUrl: string): void {
  const docDefinition = generatePdfReport(result, pageUrl);
  pdfMake.createPdf(docDefinition).download(`content-analysis-${new Date().getTime()}.pdf`);
}

/**
 * Export site analysis to PDF
 */
export function exportSiteAnalysisToPDF(result: SiteAnalysisResult, siteName: string): void {
  const docDefinition = generateSitePdfReport(result, siteName);
  pdfMake.createPdf(docDefinition).download(`site-analysis-${new Date().getTime()}.pdf`);
}
